<template>
  <div class="welcome-page">
    <div class="full-card">
      <div class="welcome-header">
        <div class="header-content">
          <img
            :src="getLogo || defaultLogo"
            alt="Company Logo"
            class="hero-img"
          />
        </div>
      </div>
      <div class="user-meta welcome-headername">
        <img
          :src="getAuthenticatedUser.avatar || defaultAvatar"
          alt="User Photo"
          class="user-photo"
        />
        <div>
          <h1>Welcome, {{ fullName }}!</h1>
        </div>
      </div>
      <div class="otp-section" v-if="loginType">
        <p class="setup-note text-center">
          <template v-if="loginType === 'OTP'">
            Enter the OTP to complete two-factor authentication.<br />
            The code was sent to
            <strong class="mfa-email">{{ getAuthenticatedUser.email }}</strong>
          </template>

          <template v-else-if="loginType === 'APP'">
            Enter the code from your authenticator app to complete two-factor
            authentication.
          </template>
        </p>

        <el-form @submit.native.prevent="verifyOTP">
          <el-form-item class="otp-input-wrapper">
            <el-input
              v-model="otp"
              maxlength="6"
              :placeholder="`Enter 6-digit ${
                loginType === 'OTP'
                  ? 'OTP'
                  : loginType === 'APP'
                  ? 'app code'
                  : 'code'
              }`"
              class="otp-input-large"
            />
          </el-form-item>

          <div class="otp-submit-wrapper">
            <el-button
              type="primary"
              :loading="verifying"
              @click="verifyOTP"
              class="otp-submit-btn"
              :disabled="submitDisabled"
            >
              Submit
            </el-button>
          </div>

          <div class="otp-resend-wrapper" v-if="loginType === 'OTP'">
            <el-button
              type="text"
              :disabled="resendDisabled"
              @click="handleSendOtp"
              class="otp-resend-btn"
            >
              Resend OTP
              <span v-if="resendCountdown"
                >({{ resendCountdown }}s)</span
              > </el-button
            ><br />

            <a
              class="fw-medium"
              style="cursor: pointer; color: #2f54eb"
              @click="logout()"
            >
              Login with different account
            </a>
          </div>

          <div class="otp-switch-wrapper" v-if="canSwitchMethod">
            <el-button type="text" @click="switchLoginType">
              <span v-if="loginType === 'OTP'"
                >Switch to Authenticator App (user MFA)</span
              >
              <span v-else>Switch to Email OTP (company MFA)</span>
            </el-button>
          </div>
        </el-form>
      </div>

      <div v-else class="text-center" style="margin-top: 2rem">
        <h3>Please wait...</h3>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { postAPICall } from "@/helpers/httpHelper";
import Swal from "sweetalert2";
import WhiteLabelConfig from "../../config/app";

import appConfig from "@/config/app";
import { clearDB } from "@/repo/appSettingRepo";
import config from "@/config/app";

export default {
  data() {
    return {
      defaultAvatar: "https://cdn-icons-png.flaticon.com/512/847/847969.png",
      defaultLogo: require("../../assets/logo.webp"),
      otp: "",
      resendDisabled: false,
      resendCountdown: 0,
      verifying: false,
      submitDisabled: true,
      otpTimer: null,
      loginType: "", // 'OTP' or 'APP'
      settings: {},
      mfaMethods: { OTP: false, APP: false },
      serverBaseURL: appConfig.AUTHFAST_BASE_URL,
    };
  },
  watch: {
    otp(newVal) {
      if (newVal.length === 6) {
        this.submitDisabled = false;
      } else {
        this.submitDisabled = true;
      }
    },
  },
  // mixins: [authHelper],
  computed: {
    ...mapGetters("company", ["getContactLoginSlug", "getSingleCompanyLogo"]),
    ...mapGetters("auth", [
      "getErrorMessage",
      "getOtpVerifiedStatus",
      "getAuthenticatedUser",
      "getActiveWorkspace",
    ]),
    fullName() {
      return `${this.getAuthenticatedUser.first_name || ""} ${
        this.getAuthenticatedUser.last_name || ""
      }`.trim();
    },
    getLogo() {
      return this.getSingleCompanyLogo
        ? `${WhiteLabelConfig.S3_BUCKET_URL}/${this.getSingleCompanyLogo}`
        : "";
    },
    canSwitchMethod() {
      return this.mfaMethods.OTP && this.mfaMethods.APP;
    },
  },
  async created() {
    await this.fetchMFASettings();
    this.setInitialLoginType();
  },
  methods: {
    logout() {
      localStorage.removeItem("botId");
      localStorage.removeItem("internal_chat_enabled");

      localStorage.removeItem("token");
      window.bus.$emit("chatbot");
      let timerInterval;
      Swal.fire({
        title: "",
        html: "E-Signs signing off....",
        timer: 3000,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        didOpen: () => {
          Swal.showLoading();
          localStorage.removeItem(config.APP_NAME + "_" + config.APP_VERSION);
          localStorage.clear();
          if (this.isSSOUser) {
            window.location.href =
              this.serverBaseURL +
              "/logout/client?redirect_url=" +
              this.baseURL;
            return;
          } else {
            this.resetStore();
          }
        },
        willClose: () => {
          clearInterval(timerInterval);
          this.resetStore();
          if (this.isSSOUser) {
            window.location.href =
              this.serverBaseURL +
              "/logout/client?redirect_url=" +
              this.baseURL;
            return;
          }
          clearDB(true);
          this.$router.push({
            path: "/signin",
          });
        },
      });
    },
    resetStore() {
      this.$store.commit("auth/resetAuthData", null, {
        root: true,
      });
      this.$store.commit("sso/resetAuthData", null, {
        root: true,
      });
      this.$store.commit("entityGroups/resetData", null, {
        root: true,
      });
      this.$store.commit("menuManagementV2/resetData", null, {
        root: true,
      });
      this.$store.commit("menuManagement/resetData", null, {
        root: true,
      });
      this.$store.commit("globalVariables/resetData", null, {
        root: true,
      });
      this.$store.commit("repeatableTemplates/resetData", null, {
        root: true,
      });
      this.$store.commit("templatesData/resetData", null, {
        root: true,
      });
      this.$store.commit("entities/resetData", null, {
        root: true,
      });
      this.$store.commit("companyTemplates/resetData", null, {
        root: true,
      });
      this.$store.commit("templateWorkflow/resetData", null, {
        root: true,
      });
      this.$store.commit("company/resetData", null, {
        root: true,
      });
      this.$store.commit("userStatus/resetData", null, {
        root: true,
      });
      this.$store.commit("subscription/resetData", null, {
        root: true,
      });
      this.$store.commit("templates/resetData", null, {
        root: true,
      });
      this.$store.commit("settings/resetData", null, {
        root: true,
      });
      this.$store.commit("dashboard/resetData", null, {
        root: true,
      });
      this.$store.commit("contactTypes/resetData", null, {
        root: true,
      });
      this.$store.commit("documentNotes/resetData", null, {
        root: true,
      });
      this.$store.commit("configurableDocuments/resetData", null, {
        root: true,
      });
      this.$store.commit("contacts/resetData", null, {
        root: true,
      });
      this.$store.commit("s3FileUpload/resetData", null, {
        root: true,
      });
      this.$store.commit("documents/resetData", null, {
        root: true,
      });
      this.$store.commit("errors/resetData", null, {
        root: true,
      });
      this.$store.commit("fileUpload/resetData", null, {
        root: true,
      });
      this.$store.commit("errors/reset", null, {
        root: true,
      });

      this.$store.commit("employeeData/reset", null, {
        root: true,
      });
      this.$store.commit("employeeDocuments/reset", null, {
        root: true,
      });
      clearDB(true);
    },
    async fetchMFASettings() {
      try {
        const user = this.getAuthenticatedUser || {};
        const companyId = user?.current_workspace?.company_id;
        const wsUserTypeId = user?.current_workspace?.user_type;
        const userMfa = !!user?.mf2a;

        this.mfaMethods = { OTP: false, APP: false };

        await Promise.all([
          this.$store.dispatch("company/fetchCompany", companyId),
          this.$store.dispatch("company/fetchCompanyLogo", companyId),
        ]);

        const roles = Array.isArray(user?.roles) ? user.roles : [];
        const activeRole = user?.activeRole;

        let roleIdForWS = null;
        if (activeRole?.userType_id === wsUserTypeId) {
          roleIdForWS = activeRole.role_id;
        } else {
          roleIdForWS =
            roles.find((r) => r.userType_id === wsUserTypeId)?.role_id || null;
        }

        const mfa = this.getContactLoginSlug?.mfa_settings || {};
        const enabled = !!mfa.enabled;

        const configuredRoleIds = Array.isArray(mfa.role_ids)
          ? [...mfa.role_ids]
          : [];

        const otpByCompany =
          enabled &&
          (configuredRoleIds.length === 0 ||
            configuredRoleIds.includes(roleIdForWS) ||
            configuredRoleIds.includes(wsUserTypeId));

        if (otpByCompany) {
          this.mfaMethods.OTP = true;
          this.settings = mfa;
        }

        if (userMfa) {
          this.mfaMethods.APP = true;
        }
      } catch (err) {
        console.error("Error fetching MFA settings:", err);
        this.mfaMethods = { OTP: false, APP: false };
      }
    },

    setInitialLoginType() {
      if (this.mfaMethods.OTP) {
        this.loginType = "OTP";
        this.handleSendOtp();
      } else if (this.mfaMethods.APP) {
        this.loginType = "APP";
      } else {
        this.$router.push({ path: "/welcome", query: this.$route.query });
      }
    },

    switchLoginType() {
      this.loginType = this.loginType === "OTP" ? "APP" : "OTP";
      this.otp = "";
      this.submitDisabled = true;
      if (this.loginType === "OTP") this.handleSendOtp();
    },

    async handleSendOtp() {
      try {
        this.otp = "";
        this.resendDisabled = true;
        this.resendCountdown = 60;
        this.submitDisabled = true;

        await postAPICall("POST", "/mfa-login/send-otp", {
          user_id: this.getAuthenticatedUser._id,
          workspace_id:
            this.getAuthenticatedUser?.current_workspace?.company_id,
          login_type: this.settings.login_type,
          email: this.getAuthenticatedUser.email,
          access_type: "admin_user",
        });

        Swal.fire({ icon: "success", title: "OTP sent to email", timer: 1500 });
        this.submitDisabled = false;

        this.otpTimer = setTimeout(() => {
          this.submitDisabled = true;
        }, 300000);

        const resendTimer = setInterval(() => {
          this.resendCountdown--;
          if (this.resendCountdown <= 0) {
            clearInterval(resendTimer);
            this.resendDisabled = false;
          }
        }, 1000);
      } catch (err) {
        console.log("Otp err", err);
        Swal.fire({
          icon: "error",
          title: err?.response?.data?.message || "Failed to send OTP",
        });
        this.resendDisabled = false;
      }
    },

    async verifyOTP() {
      if (!this.otp || this.otp.length !== 6) {
        Swal.fire({ icon: "warning", title: "Enter a valid 6-digit code" });
        return;
      }

      if (this.loginType === "OTP" && this.submitDisabled) {
        Swal.fire({ icon: "error", title: "OTP expired. Please resend." });
        return;
      }

      this.verifying = true;

      try {
        const userId = this.getAuthenticatedUser._id;

        if (this.loginType === "OTP") {
          const payload = {
            user_id: userId,
            workspace_id:
              this.getAuthenticatedUser?.current_workspace?.company_id,
            otp: this.otp,
            login_type: this.settings.login_type,
            email: this.getAuthenticatedUser.email,
            access_type: "admin_user",
          };

          const res = await postAPICall(
            "POST",
            "/mfa-login/verify-otp",
            payload
          );
          if (res?.success) {
            Swal.fire({
              icon: "success",
              title: "Verification successful",
              timer: 1500,
            });
            this.$router.push({ path: "/welcome", query: this.$route.query });
          } else {
            Swal.fire({ icon: "error", title: res?.message || "Invalid OTP" });
          }
        } else if (this.loginType === "APP") {
          await this.$store.dispatch("auth/verify_MF2A_Otp", {
            token: this.otp,
            user_id: userId,
          });

          if (this.getOtpVerifiedStatus) {
            Swal.fire({ icon: "success", title: "OTP Verified", timer: 1500 });
            this.$router.push({ path: "/welcome", query: this.$route.query });
          } else {
            Swal.fire({
              icon: "error",
              title: this.getErrorMessage || "Invalid code",
            });
          }
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: err?.response?.data?.message || "Verification failed",
        });
      } finally {
        this.verifying = false;
      }
    },
  },
};
</script>

<style scoped>
.otp-switch-wrapper {
  text-align: center;
  margin-top: 1rem;
}
</style>

<style scoped>
.welcome-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1rem;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  z-index: 1;

  background-color: rgb(255 255 255 / 15%);
}

.welcome-page::before {
  content: "";
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: -2;
  background-image: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)),
    url("../../assets/7714.jpg"), linear-gradient(160deg, #1e3c72, #2a5298);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  filter: brightness(1);
}

@keyframes panBackground {
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 100% 100%;
  }
}

.welcome-header {
  text-align: center;
  max-width: 600px;
  margin-bottom: 2rem;

  h1 {
    color: #2f3169;
  }
}
.welcome-headername {
  text-align: center;

  h1 {
    color: #2f3169;
  }
}
.hero-img {
  width: 280px;
  margin-bottom: 1rem;
}

.setup-note {
  color: grey;
  font-weight: 600;
  font-size: 0.85rem;
  margin-top: 0.5rem;
}

.steps-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  width: 100%;
}
.full-card {
  align-items: center;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05);
  padding: 1rem 1.2rem;
  transition: background 0.3s ease;

  background-color: rgb(255 255 255 / 99%);
  border: 1px solid rgb(30 2 2 / 20%);
  backdrop-filter: blur(6px);
  color: #fff;
  padding-left: 7%;
  padding-right: 7%;
}
.step-card {
  display: flex;
  align-items: center;
  border-radius: 12px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05);
  padding: 1rem 1.2rem;
  transition: background 0.3s ease;

  background: #00000008;
  border: 1px solid rgb(23 37 138 / 39%);
  backdrop-filter: blur(6px);
  color: #195d8e;
  box-shadow: inset 0 0 1px #fff3, 0 1px 4px rgba(0, 0, 0, 0.2);
}

.icon-wrapper {
  width: 32px;
  height: 32px;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader {
  border: 3px solid #212931;
  border-top-color: transparent;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  animation: spin 1s linear infinite;
}

.step-done {
  font-size: 24px;
  color: #4caf50;
}

.step-description {
  font-size: 1.1rem;
  font-weight: 500;
  color: #101010;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.setup-wrapper {
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  max-width: 600px;
  margin: 0 auto;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 2rem;
  justify-content: center;
  flex-wrap: wrap;
}

.user-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-photo {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid #ffffffcc;
  object-fit: cover;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.otp-section {
  margin-top: 1.5rem;
  width: 100%;
}

.otp-input-wrapper {
  display: flex;
  justify-content: center;
}

.otp-input {
  text-align: center;
  font-size: 1.2rem;
  letter-spacing: 4px;
  max-width: 250px;
}

.otp-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 0.5rem;
}

.setup-note {
  color: grey;
  font-weight: 600;
  font-size: 0.95rem;
  margin-top: 0.5rem;
  text-align: center;
}

.otp-input-wrapper {
  display: flex;
  justify-content: center;
}

.otp-submit-wrapper {
  text-align: center;
  margin-top: 1rem;
}

.otp-submit-btn {
  width: 66%;
}

.otp-resend-wrapper {
  text-align: center;
  margin-top: 0.5rem;
}

.otp-resend-btn {
  font-size: 0.9rem;
  color: #2f54eb;
}
.otp-input-large {
  text-align: center;

  width: 400px;
}

.otp-submit-btn {
  width: 100%;
  width: 400px;
}

@media (max-width: 600px) {
  .otp-input-large {
    width: 100%;

    font-size: 1rem;
  }
  .otp-submit-btn {
    width: 70%;

    font-size: 1rem;
  }
}

.mfa-email {
  overflow-wrap: anywhere;
  word-break: break-word;
}

@media (max-width: 480px) {
  .setup-note {
    font-size: 0.95rem;
    line-height: 1.4;
    padding: 0 0.5rem;
  }
}
</style>
